import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Typography, Row, Col, Space, Input, Checkbox, Modal } from 'antd';
import { Link } from 'react-router-dom';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { CommonFormProps, OnboardingLayout } from '.';
import { phoneValidation } from '~/utils';
import { Form } from '~/components';
import { beginSmsOtp } from '~/api/onboarding';
import { auth } from '~/api/axios';
import branding from '~/branding';
import { useOnboardingPage, useOnboardingState, useResetOnboardingState } from '~/hooks/onboarding';
import onError from '~/errorsHandler';
import CaptchaInput from '~/components/inputs/CaptchaInput';
import features from '~/featuresWithDefaults';
import PhoneInput from '~/components/PhoneInput';

const { Title, Text } = Typography;

export type PersonalInfoRelatedData = {
  isAgree?: boolean;
  challengeToken?: string;
  readonlyFirstName?: string;
  readonlyLastName?: string;
  readonlyPhoneNumber?: string;
};

const PersonalInfo: FC<CommonFormProps> = (props) => {
  const { name } = props;
  const { t } = useTranslation('onboarding');
  const dt = (key: string): string => t(`translation:${key}`);
  const [isLoading, setLoading] = useState(false);

  const [onboardingState] = useOnboardingState();
  const [pageState, setPageState] = useOnboardingState<any, PersonalInfoRelatedData>(name);
  const readonlyFirstName = pageState.relatedData?.readonlyFirstName;
  const readonlyLastName = pageState.relatedData?.readonlyLastName;
  const readonlyPhoneNumber = pageState.relatedData?.readonlyPhoneNumber;

  const resetOnboardingState = useResetOnboardingState();
  const onboardingPage = useOnboardingPage(name);
  const [form] = Form.useForm();

  // Legal agree
  const isAgree = pageState.relatedData?.isAgree;
  const toggleCheckbox = () => {
    setPageState((prev) => {
      return {
        ...prev,
        relatedData: {
          ...(prev.relatedData || {}),
          isAgree: !prev.relatedData?.isAgree,
        },
      };
    });
  };

  const smsOtp = async (values: any) => {
    const { phoneNumber, captchaToken } = values;
    setLoading(true);
    try {
      const {
        data: { challengeToken },
      } = await beginSmsOtp({
        phoneNumber,
        captchaToken,
      });
      auth.deleteToken();
      setPageState((prev) => {
        return {
          ...prev,
          formData: values,
          relatedData: {
            ...(prev.relatedData || {}),
            challengeToken,
          },
        };
      });
      onboardingPage.activateNext();
      onboardingPage.goNext();
    } catch (error: any) {
      onError(error);
    }
    setLoading(false);
  };

  const onFinish = (values: any) => {
    const enabledCount = Object.values(onboardingState).filter((item) => !item.disabled).length;
    console.log(Object.values(onboardingState).filter((item) => !item.disabled));
    if (enabledCount > 2) {
      Modal.confirm({
        title: t('startFromBegining'),
        icon: <ExclamationCircleOutlined />,
        content: t('changesWillLost'),
        okText: dt('confirm'),
        cancelText: dt('cancel'),
        onOk() {
          auth.deleteToken();
          resetOnboardingState();
          setPageState((prev) => ({
            ...prev,
            formData: { ...values },
            relatedData: {
              isAgree: true,
            },
          }));
          return smsOtp(values);
        },
      });
    } else {
      auth.deleteToken();
      void smsOtp(values);
    }
  };

  return (
    <OnboardingLayout>
      <Title level={2}>{t('personalInfoHeader')}</Title>
      <Space size="middle" direction="vertical">
        <Text>{t('personalInfoSubtitle')}</Text>
        <Form
          form={form}
          initialValues={pageState.formData || undefined}
          layout="vertical"
          size="large"
          onFinish={onFinish}
        >
          <Input.Group>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  rules={[{ required: true }]}
                  initialValue={readonlyFirstName}
                >
                  <Input placeholder={dt('firstName')} disabled={!!readonlyFirstName} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  rules={[{ required: true }]}
                  initialValue={readonlyLastName}
                >
                  <Input placeholder={dt('lastName')} disabled={!!readonlyLastName} />
                </Form.Item>
              </Col>
            </Row>
          </Input.Group>
          <Form.Item
            name="phoneNumber"
            rules={[{ required: true, validator: phoneValidation }]}
            initialValue={readonlyPhoneNumber}
          >
            <PhoneInput placeholder={dt('phoneNumber')} disabled={!!readonlyPhoneNumber} />
          </Form.Item>
          <Form.Item>
            <Checkbox checked={isAgree} onChange={toggleCheckbox}>
              <Trans i18nKey="onboarding:I agree with <1>{{name}}<1/>">
                {'I agree with '}
                <a className="underline" href={branding.termsAndConditionsURL} target="__blank">
                  {{ name: t('terms and conditions') }}
                </a>
              </Trans>
            </Checkbox>
          </Form.Item>
          {features?.showCaptcha && (
            <Form.Item name="captchaToken" rules={[{ required: true }]}>
              <CaptchaInput />
            </Form.Item>
          )}
          <Row gutter={18} align="middle" wrap={false}>
            <Col flex="none">
              <Form.Item noStyle>
                <Button type="primary" htmlType="submit" loading={isLoading} disabled={!isAgree}>
                  {t('getStarted')}
                </Button>
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Link to="/login">{t('signIn')}</Link>
            </Col>
          </Row>
        </Form>
      </Space>
    </OnboardingLayout>
  );
};

export default PersonalInfo;
